import { useRef } from 'react'

import { useI18n, type MessageKey } from '../lib/i18n'
import { useAnchoredPopover } from '../lib/popover'
import { REMEMBER_DAYS, useSiteToken, type TokenState } from '../lib/token'
import { Icon } from './Icon'
import { ErrorBox, Field } from './ui'

/** The panel's id, so the upload page can open it with `popoverTarget` while no token is set. */
export const TOKEN_PANEL = 'token-panel'

const LABELS: Record<TokenState, MessageKey> = {
  none: 'tokenNotSet',
  invalid: 'tokenNotRecognized',
  checking: 'tokenChecking',
  unknown: 'tokenNotRecognized',
  set: 'tokenSet',
}
const TONES: Record<TokenState, string> = { none: 'warn', invalid: 'danger', checking: 'quiet', unknown: 'danger', set: 'ok' }

/**
 * The site token at the navigation's foot: whether one is set and for which site, so it is clear
 * before an upload, which needs it (TOK-06, TOK-07). It opens the panel to enter, remember or forget
 * it. Users enrolled for no token cannot upload and do not see it.
 */
export function TokenStatus({ collapsed }: { collapsed: boolean }) {
  const token = useSiteToken()
  return token.keys.data?.length || token.state !== 'none' ? <TokenEntry collapsed={collapsed} /> : null
}

function TokenEntry({ collapsed }: { collapsed: boolean }) {
  const { t } = useI18n()
  const token = useSiteToken()
  const { trigger, popover } = useAnchoredPopover<HTMLDivElement>('start', TOKEN_PANEL)
  const input = useRef<HTMLInputElement>(null)
  const label = t(LABELS[token.state])
  const detail = token.match?.site_name ?? (token.state === 'checking' ? undefined : t('uploadsBlocked'))
  return (
    <>
      <button
        {...trigger}
        type="button"
        className={`nav-item token-status ${TONES[token.state]}`}
        title={collapsed ? [label, detail].filter(Boolean).join(' · ') : undefined}
      >
        <Icon name="key" />
        <span className="nav-label">
          {label}
          {detail && <small>{detail}</small>}
        </span>
      </button>
      <div
        {...popover}
        role="dialog"
        aria-labelledby="token-panel-title"
        className="token-panel"
        onToggle={(event) => {
          if (event.newState !== 'open') return
          token.keys.reload() // an enrollment made meanwhile counts
          input.current?.focus()
        }}
      >
        <h2 id="token-panel-title" className="with-icon">
          <Icon name="key" />
          {t('anonymizationToken')}
        </h2>
        <p className="muted small">{t('tokenPanelIntro')}</p>
        <Field label={t('token')} hint={token.state === 'invalid' ? t('tokenInvalid') : token.locked ? t('tokenLocked') : undefined}>
          <input
            ref={input}
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={token.token}
            disabled={token.locked}
            onChange={(event) => token.setToken(event.target.value)}
          />
        </Field>
        {(token.state === 'set' || token.state === 'unknown') && (
          <p className={token.match ? 'success small' : 'alert small'} role="status">
            {token.match ? t('tokenMatches', { site: token.match.site_name }) : t('tokenNoMatch')}
          </p>
        )}
        <label className="check small">
          <input type="checkbox" checked={token.remember} disabled={token.locked} onChange={(event) => token.setRemember(event.target.checked)} />
          {t('rememberToken', { days: String(REMEMBER_DAYS) })}
        </label>
        <ErrorBox error={token.keys.error} />
        <div className="actions">
          {token.token && (
            <button type="button" className="secondary" disabled={token.locked} onClick={token.forget}>
              {t('forgetToken')}
            </button>
          )}
          <button type="button" popoverTarget={TOKEN_PANEL} popoverTargetAction="hide">
            {t('close')}
          </button>
        </div>
      </div>
    </>
  )
}
